import { useState, useEffect, useMemo } from "react";
import { useSelector } from "react-redux";
import { useNavigate, Link } from "react-router-dom";
import { Navbar } from "./navbar";
import { CreditCard, Wallet, Package, ArrowLeft, User as UserIcon, MapPin, Smartphone } from "lucide-react";
import "../styles/validationCommande.css";

export function ValidationCommande() {
    const navigate = useNavigate();
    const loggedUser = localStorage.getItem('user');
    const userEmail = loggedUser ? JSON.parse(loggedUser).email : null;
    const list = (useSelector(s => s.panier) || []).filter(p => p.userEmail === userEmail);

    // Filter only checked items for the order summary
    const checkedItems = useMemo(() => list.filter(i => i.checked), [list]);

    // Calculate total precisely
    const total = useMemo(() => {
        return checkedItems.reduce((sum, item) => {
            const price = typeof item.prix === 'string'
                ? parseFloat(item.prix.replace(/[^0-9.]/g, ''))
                : item.prix;
            const qty = item.quantite || 1;
            return sum + (isNaN(price) ? 0 : price * qty);
        }, 0);
    }, [checkedItems]);

    const [formData, setFormData] = useState({
        nom: "",
        adresse: "",
        ville: "",
        telephone: ""
    });

    const [paiementMethod, setPaiementMethod] = useState("");

    // Initialization: Check for items and pre-fill user name
    useEffect(() => {
        // Redirection if cart is empty or no items are checked
        if (checkedItems.length === 0) {
            navigate("/panier");
            return;
        }

        const savedUser = localStorage.getItem('user');
        if (savedUser) {
            try {
                const parsedUser = JSON.parse(savedUser);
                if (parsedUser.name) {
                    setFormData(prev => ({ ...prev, nom: parsedUser.name }));
                }
            } catch (e) {
                console.error("Error parsing user from localStorage", e);
            }
        }
    }, [checkedItems.length, navigate]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleFormSubmit = (e) => {
        e.preventDefault();

        if (!paiementMethod) {
            alert("Veuillez choisir une méthode de paiement pour continuer.");
            return;
        }

        // Navigate to payment processor with detailed state
        navigate("/paiement", {
            state: {
                method: paiementMethod,
                total: total,
                orderInfo: formData,
                itemsCount: checkedItems.length,
                items: checkedItems
            }
        });
    };

    return (
        <div className="validation-page">
            <Navbar />

            <main className="validation-container">
                <header className="validation-header">
                    <Link to="/panier" className="back-to-cart-link">
                        <ArrowLeft size={18} />
                        <span>Retour au panier</span>
                    </Link>
                    <h1>Finaliser votre commande</h1>
                    <p>Dernière étape avant la confirmation de vos articles.</p>
                </header>

                <div className="validation-content">
                    {/* Form Section */}
                    <div className="validation-form-side">
                        <form onSubmit={handleFormSubmit} className="checkout-form">

                            <div className="form-section">
                                <h3 className="section-title">
                                    <MapPin size={20} className="title-icon" />
                                    Informations de Livraison
                                </h3>

                                <div className="form-row">
                                    <div className="input-group">
                                        <label htmlFor="nom">Nom complet</label>
                                        <div className="input-wrapper">
                                            <UserIcon size={18} className="input-icon" />
                                            <input
                                                id="nom"
                                                type="text"
                                                name="nom"
                                                value={formData.nom}
                                                onChange={handleChange}
                                                required
                                                placeholder="Ex: Ahmed Alami"
                                            />
                                        </div>
                                    </div>
                                    <div className="input-group">
                                        <label htmlFor="telephone">Téléphone</label>
                                        <div className="input-wrapper">
                                            <Smartphone size={18} className="input-icon" />
                                            <input
                                                id="telephone"
                                                type="tel"
                                                name="telephone"
                                                value={formData.telephone}
                                                onChange={handleChange}
                                                required
                                                placeholder="+212 600..."
                                            />
                                        </div>
                                    </div>
                                </div>

                                <div className="input-group full-width">
                                    <label htmlFor="adresse">Adresse de livraison</label>
                                    <textarea
                                        id="adresse"
                                        name="adresse"
                                        value={formData.adresse}
                                        onChange={handleChange}
                                        required
                                        placeholder="N°, Rue, Quartier, Appartement..."
                                        rows="3"
                                    />
                                </div>

                                <div className="input-group">
                                    <label htmlFor="ville">Ville</label>
                                    <input
                                        id="ville"
                                        type="text"
                                        name="ville"
                                        value={formData.ville}
                                        onChange={handleChange}
                                        required
                                        placeholder="Ex: Casablanca"
                                    />
                                </div>
                            </div>

                            <div className="form-section mt-40">
                                <h3 className="section-title">
                                    <CreditCard size={20} className="title-icon" />
                                    Méthode de Paiement
                                </h3>

                                <div className="payment-options">
                                    <label className={`payment-option ${paiementMethod === 'visa' ? 'selected' : ''}`}>
                                        <input
                                            type="radio"
                                            name="paiement"
                                            value="visa"
                                            checked={paiementMethod === 'visa'}
                                            onChange={(e) => setPaiementMethod(e.target.value)}
                                        />
                                        <div className="payment-method-icon"><CreditCard size={28} /></div>
                                        <div className="payment-method-details">
                                            <h4>Carte Bancaire</h4>
                                            <p>Visa, Mastercard, CMI</p>
                                        </div>
                                    </label>

                                    <label className={`payment-option ${paiementMethod === 'paypal' ? 'selected' : ''}`}>
                                        <input
                                            type="radio"
                                            name="paiement"
                                            value="paypal"
                                            checked={paiementMethod === 'paypal'}
                                            onChange={(e) => setPaiementMethod(e.target.value)}
                                        />
                                        <div className="payment-method-icon"><Wallet size={28} /></div>
                                        <div className="payment-method-details">
                                            <h4>PayPal</h4>
                                            <p>Compte PayPal sécurisé</p>
                                        </div>
                                    </label>

                                    <label className={`payment-option ${paiementMethod === 'livraison' ? 'selected' : ''}`}>
                                        <input
                                            type="radio"
                                            name="paiement"
                                            value="livraison"
                                            checked={paiementMethod === 'livraison'}
                                            onChange={(e) => setPaiementMethod(e.target.value)}
                                        />
                                        <div className="payment-method-icon"><Package size={28} /></div>
                                        <div className="payment-method-details">
                                            <h4>Paiement à la livraison</h4>
                                            <p>Payer cash dès réception</p>
                                        </div>
                                    </label>
                                </div>
                            </div>

                            <button type="submit" className="btn-valider-checkout">
                                <span>Procéder au paiement ({total.toLocaleString()} DH)</span>
                                <ArrowLeft size={20} className="rotate-180" />
                            </button>
                        </form>
                    </div>

                    {/* Summary Section */}
                    <aside className="validation-summary-side">
                        <div className="checkout-summary-card">
                            <h3 className="summary-card-title">Résumé de commande</h3>

                            <div className="checkout-items-preview">
                                {checkedItems.map(item => (
                                    <div key={item.id} className="preview-item">
                                        <div className="preview-img-container">
                                            <img src={item.image} alt={item.nomClimatisseur} />
                                            <span className="item-qty-badge">{item.quantite || 1}</span>
                                        </div>
                                        <div className="preview-item-info">
                                            <h5>{item.nomClimatisseur}</h5>
                                            <p className="preview-item-meta">{item.taille} {item.color ? `| ${item.color}` : ''}</p>
                                            <p className="preview-price">{item.prix} DH</p>
                                        </div>
                                    </div>
                                ))}
                            </div>

                            <div className="checkout-totals">
                                <div className="checkout-row">
                                    <span>Sous-total</span>
                                    <span>{total.toLocaleString()} DH</span>
                                </div>
                                <div className="checkout-row">
                                    <span>Frais de livraison</span>
                                    <span className="free-shipping">Gratuit</span>
                                </div>
                                <div className="checkout-total-row">
                                    <span>Montant total</span>
                                    <span className="final-amount">{total.toLocaleString()} DH</span>
                                </div>
                            </div>

                            <div className="secure-badge">
                                <CreditCard size={14} />
                                <span>Paiement 100% sécurisé</span>
                            </div>
                        </div>
                    </aside>
                </div>
            </main>
        </div>
    );
}